"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActionScene = void 0;
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
const Scene_1 = require("./Scene");
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const interfaces_1 = require("../../../shared/interfaces");
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const blocks_1 = require("../blocks");
const gsap_core_1 = require("gsap/gsap-core");
const settings_1 = require("../config/settings");
const lodash_1 = __importDefault(require("lodash"));
const i18next_1 = __importDefault(require("i18next"));
const hourglass_svg_1 = __importDefault(require("../../assets/svg/hourglass.svg"));
const brushpoint_svg_1 = __importDefault(require("../../assets/svg/brushpoint.svg"));
const next_svg_1 = __importDefault(require("../../assets/svg/next.svg"));
const IconButton_1 = require("../blocks/IconButton");
const ui_1 = require("@pixi/ui");
const SkipPopup_1 = require("../popups/SkipPopup");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class ActionScene extends Scene_1.Scene {
    constructor(app, sceneManager, gameManager) {
        var _a;
        super(app, sceneManager, gameManager, (0, Global_1.colorForGameType)((_a = gameManager.roomStream.round$.value) === null || _a === void 0 ? void 0 : _a.type));
        this.skiptimer = null;
        this.roundtimer = null;
        this.sortableChildren = true;
        this.round = this.gameManager.roomStream.round$.value;
        if (!this.isValid) {
            // Shouldn't be here, go home instead, fast!
            this.sceneManager.routeTo(SceneManager_1.Scenes.Home, null, true).then();
            return;
        }
        this.myCanvas = this.makeCanvas();
        this.leftStatus = this.makeLeftStatus();
        this.rightStatus = this.makeRightStatus();
        this.leftStatus.zIndex = this.rightStatus.zIndex = 2;
        // Opponent canvases
        const ops = this.gameManager.opponents;
        if (ops === null || ops === void 0 ? void 0 : ops.length) {
            this.opponents = {};
            for (let i = 0; i < config_json_1.default.roomsize - 1; i++) {
                if (ops[i]) {
                    const opp = this.makeOpponent(ops[i].pkey);
                    opp.zIndex = 2;
                    this.opponents[ops[i].pkey] = opp;
                }
            }
        }
        this.subs.add(this.gameManager.roomStream.roundfinish$.subscribe({
            next: (item) => __awaiter(this, void 0, void 0, function* () {
                yield this.finish(item.pkey);
            })
        }));
        this.subs.add(this.gameManager.roomStream.roundcancel$.subscribe({
            next: () => __awaiter(this, void 0, void 0, function* () {
                yield this.finish();
            })
        }));
        const leaveTimeouts = new Map();
        this.subs.add(this.gameManager.roomStream.playerleave$.subscribe({
            next: (player) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                if (player) {
                    if (player.pkey === this.gameManager.playerManager.pkey && !this.isDeinited) {
                        // Wat? We left? Maybe we lost internet connection
                        console.log(this.gameManager.playerManager.pkey + ' just left own room: ' + ((_b = this.round) === null || _b === void 0 ? void 0 : _b.rkey));
                    }
                    else {
                        const timeoutId = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                            yield this.series(() => __awaiter(this, void 0, void 0, function* () { return yield this.dropOpponent(player); }));
                            leaveTimeouts.delete(player.pkey); // Clean up after the player is dropped
                        }), config_json_1.default.pingcheck);
                        leaveTimeouts.set(player.pkey, timeoutId);
                    }
                }
            })
        }));
        this.subs.add(this.gameManager.roomStream.playerenter$.subscribe({
            next: (player) => __awaiter(this, void 0, void 0, function* () {
                if (!this.isDeinited && player && player.pkey !== this.gameManager.playerManager.pkey) {
                    const timeoutId = leaveTimeouts.get(player.pkey);
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                        leaveTimeouts.delete(player.pkey);
                    }
                    yield this.series(() => __awaiter(this, void 0, void 0, function* () { return yield this.addOpponent(player); }));
                }
            })
        }));
    }
    makeCanvas() {
        const canvas = new blocks_1.DrawCanvas(this.app, this.canvasoptions);
        canvas.zIndex = 1;
        return this.addChild(canvas);
    }
    ding() {
        AssetManager_1.default.dingSound();
    }
    thud() {
        AssetManager_1.default.thudSound();
    }
    finish(winner = null) {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            clearTimeout(this.skiptimer);
            this.myCanvas.enabled = false;
            // Even if this isn't called we have an emergency one on the backend (with a timeout), it is just nice to have
            // everyone see the animations first
            const textsize = 32;
            const duration = 0.3;
            const style = blocks_1.Block.textStyle(textsize);
            style.align = 'center';
            style.wordWrap = true;
            style.wordWrapWidth = this.myCanvas.width - (0, Global_1.px)(60);
            const text = new pixi_js_1.Text(null, style);
            text.zIndex = 3;
            text.anchor.set(0.5, 0);
            this.addChild(text);
            const inText = () => __awaiter(this, void 0, void 0, function* () {
                text.alpha = 0;
                yield this.promisedTimeline(gsap_core_1.gsap.timeline().to(text, {
                    pixi: {
                        alpha: 1
                    },
                    duration: duration
                }));
            });
            const iwon = winner === this.gameManager.playerManager.pkey;
            const other = winner && this.opponents ? this.opponents[winner] : null;
            if (this.gameManager.playerManager.options$.value.vibrate) {
                (0, Global_1.vibrate)();
            }
            yield Promise.all([
                (0, Global_1.fade)(this.leftStatus), (0, Global_1.fade)(this.rightStatus),
                this.myCanvas.inOverlay(iwon ? settings_1.Settings.successColor : settings_1.Settings.failColor, duration)
            ]
                .concat(lodash_1.default.values(this.opponents || {}).map((o) => o.inOverlay(o.pkey === winner ? settings_1.Settings.successColor : settings_1.Settings.failColor, duration))));
            switch ((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) {
                case interfaces_1.GameType.Battle:
                case interfaces_1.GameType.BattleFriends:
                case interfaces_1.GameType.Tutorial:
                case interfaces_1.GameType.Guess:
                case interfaces_1.GameType.GuessFriends:
                    // Text animation
                    const label = (0, Global_1.category)((_b = this.round) === null || _b === void 0 ? void 0 : _b.label);
                    text.alpha = 0;
                    if (((_c = this.round) === null || _c === void 0 ? void 0 : _c.type) === interfaces_1.GameType.Tutorial) {
                        if (iwon) {
                            text.text = i18next_1.default.t('tutorial.youdrew', { value: label });
                        }
                        else {
                            text.text = i18next_1.default.t('practice.skipped');
                        }
                        this.ding();
                    }
                    else {
                        const ikey = (this.round.type === interfaces_1.GameType.Guess || this.round.type === interfaces_1.GameType.GuessFriends) ? 'guesser' : 'battle';
                        if (iwon) {
                            text.text = i18next_1.default.t(ikey + '.youdrew', { value: label });
                            this.ding();
                        }
                        else if (other === null || other === void 0 ? void 0 : other.pkey) {
                            text.text = i18next_1.default.t(ikey + '.otherdrew', {
                                other: (_d = this.gameManager.opponent(other.pkey)) === null || _d === void 0 ? void 0 : _d.u,
                                value: label, interpolation: { escapeValue: false }
                            });
                            this.thud();
                        }
                        else { // No winner
                            text.text = i18next_1.default.t(ikey + '.cancel', { value: label });
                            // hourglass
                            const glass = pixi_js_1.Sprite.from(hourglass_svg_1.default);
                            glass.zIndex = 3;
                            glass.anchor.set(0.5);
                            glass.scale.set((0, Global_1.px)(this.overSmallSize ? 0.3 : 0.21));
                            glass.position.set(this.center.x, this.myCanvas.y + this.myCanvas.height / 3);
                            this.addChild(glass);
                            this.thud();
                        }
                    }
                    text.style.fill = iwon ? settings_1.Settings.whiteColor : settings_1.Settings.blackColor;
                    text.style.lineHeight = (0, Global_1.px)(this.overSmallSize ? textsize * 1.5 : textsize * 1.2);
                    text.position.set(this.center.x, this.myCanvas.y + this.myCanvas.height / 2);
                    yield inText();
                    // Brush Animation
                    if (iwon) {
                        const win = pixi_js_1.Sprite.from(brushpoint_svg_1.default);
                        win.rotation = Math.PI;
                        win.anchor.set(0.5);
                        win.scale.set((0, Global_1.px)(0.1));
                        win.position.set(this.myCanvas.width / 2, this.myCanvas.height / 3);
                        yield this.inBrush(this.myCanvas.addChild(win), 1, 0.8);
                    }
                    else if (other) {
                        const win = other.makeBrush();
                        yield this.inBrush(win, other.midscale, other.endscale);
                    }
                    break;
                case interfaces_1.GameType.Speed:
                    text.alpha = 0;
                    const cat = i18next_1.default.t('categories.' + ((_e = this.round) === null || _e === void 0 ? void 0 : _e.label) + '.n');
                    text.text = i18next_1.default.t('speed.youdrew', { value: cat, round: (_f = this.round) === null || _f === void 0 ? void 0 : _f.state, rounds: config_json_1.default.speedrounds });
                    text.style.fill = settings_1.Settings.whiteColor;
                    text.anchor.set(0.5);
                    text.style.lineHeight = (0, Global_1.px)(this.overSmallSize ? textsize * 1.8 : textsize * 1.6);
                    text.position.set(this.center.x, this.myCanvas.y + this.myCanvas.height / 2);
                    this.ding();
                    yield inText();
                    break;
                case interfaces_1.GameType.Practice:
                case interfaces_1.GameType.Levels:
                    text.alpha = 0;
                    if (iwon) {
                        text.text = i18next_1.default.t('practice.youdrew', { value: (0, Global_1.category)((_g = this.round) === null || _g === void 0 ? void 0 : _g.label) });
                        text.style.fill = settings_1.Settings.whiteColor;
                    }
                    else {
                        text.text = i18next_1.default.t('practice.skipped');
                        text.style.fill = settings_1.Settings.blackColor;
                    }
                    this.ding();
                    text.position.set(this.center.x, this.myCanvas.y + this.myCanvas.height / 2);
                    yield inText();
                    break;
            }
            yield (0, Global_1.sleep)(1.2);
            // Next Round
            this.gameManager.notifyReady();
        });
    }
    inBrush(win, midscale = 1, endscale = 0.8) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                return yield this.promisedTimeline(gsap_core_1.gsap.timeline().to(win, {
                    pixi: {
                        scale: (0, Global_1.px)(midscale),
                        rotation: 0
                    },
                    duration: 0.45,
                    ease: 'power2.out'
                }).to(win, {
                    pixi: {
                        scale: (0, Global_1.px)(endscale)
                    },
                    duration: 0.18,
                    ease: 'power1.in'
                }));
            }
            catch (e) {
                console.log(e);
            }
        });
    }
    addOpponent(player) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.opponents) {
                this.opponents = {};
            }
            let opponent = this.opponents[player.pkey];
            if (!opponent) {
                opponent = this.makeOpponent(player.pkey);
                opponent.visible = false;
                this.opponents[player.pkey] = opponent;
                this.layout();
            }
            return opponent;
        });
    }
    dropOpponent(player) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.opponents && this.opponents[player.pkey]) {
                this.opponents[player.pkey].visible = false;
                return this.opponents[player.pkey];
            }
            return null;
        });
    }
    makeLeftStatus() {
        var _a, _b, _c, _d, _e, _f;
        const container = new pixi_js_1.Container();
        switch ((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) {
            case interfaces_1.GameType.Battle:
            case interfaces_1.GameType.BattleFriends:
            case interfaces_1.GameType.GuessFriends:
            case interfaces_1.GameType.Guess:
                // We need our brush score
                const pad = (0, Global_1.px)(10);
                for (let i = 0; i < this.gameManager.score() && i < config_json_1.default.battlescore; i++) {
                    const brushScore = pixi_js_1.Sprite.from(brushpoint_svg_1.default);
                    brushScore.scale.set((0, Global_1.px)(0.35));
                    brushScore.x = pad + (i * (0, Global_1.px)(26));
                    container.addChild(brushScore);
                }
                break;
            case interfaces_1.GameType.Speed:
            case interfaces_1.GameType.Practice:
            case interfaces_1.GameType.Tutorial:
                // Just count the rounds
                const style = blocks_1.Block.textStyle(32);
                style.fill = settings_1.Settings.grayColor;
                style.align = 'left';
                let txt = '#' + ((_b = this.round) === null || _b === void 0 ? void 0 : _b.state);
                if (((_c = this.round) === null || _c === void 0 ? void 0 : _c.type) === interfaces_1.GameType.Tutorial) {
                    txt = ((_d = this.round) === null || _d === void 0 ? void 0 : _d.state) + '/' + config_json_1.default.tutorial.length;
                }
                else if (((_e = this.round) === null || _e === void 0 ? void 0 : _e.type) === interfaces_1.GameType.Speed) {
                    txt = ((_f = this.round) === null || _f === void 0 ? void 0 : _f.state) + '/' + config_json_1.default.speedrounds;
                }
                // else if(this.round?.type === GameType.Guess){
                //     txt = this.round?.state + '/' + config.guessrounds;
                // }
                const text = new pixi_js_1.Text(txt, style);
                text.position.set((0, Global_1.px)(7));
                container.addChild(text);
                break;
        }
        return this.addChild(container);
    }
    makeRightStatus() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const container = new pixi_js_1.Container();
        const second = i18next_1.default.t('second');
        const minute = i18next_1.default.t('minute');
        const style = blocks_1.Block.textStyle(32, false);
        style.align = 'right';
        style.fill = settings_1.Settings.grayColor;
        if (((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) === interfaces_1.GameType.Battle || ((_b = this.round) === null || _b === void 0 ? void 0 : _b.type) === interfaces_1.GameType.Guess ||
            ((_c = this.round) === null || _c === void 0 ? void 0 : _c.type) === interfaces_1.GameType.GuessFriends ||
            ((_d = this.round) === null || _d === void 0 ? void 0 : _d.type) === interfaces_1.GameType.BattleFriends) {
            // We need our round timer
            const totaltime = Math.round((this.cuttime - Date.now()) / 1000);
            const text = new pixi_js_1.Text(totaltime + second, style);
            text.visible = false;
            text.anchor.set(1.0, 0);
            container.addChild(text);
            const countdown = () => {
                const diff = this.cuttime - Date.now();
                if (diff <= 0) {
                    // clearInterval(this.roundtimer);
                    text.visible = false;
                }
                else {
                    text.text = Math.round(diff / 1000) + second;
                    if (!text.visible) {
                        (0, Global_1.fade)(text, 1).then();
                    }
                }
            };
            this.roundtimer = setInterval(countdown, 200);
            if (totaltime > 0) {
                (0, Global_1.fade)(text, 1).then();
            }
        }
        else if (((_e = this.round) === null || _e === void 0 ? void 0 : _e.type) === interfaces_1.GameType.Speed) {
            let start = Date.now();
            if (((_f = this.room) === null || _f === void 0 ? void 0 : _f.state) && ((_g = this.room.rounds[this.room.state]) === null || _g === void 0 ? void 0 : _g.time)) {
                start = Math.floor(((_h = this.room.rounds[this.room.state]) === null || _h === void 0 ? void 0 : _h.time) / 1000);
            }
            const cur = this.room.time / 1000;
            const timestr = (milli) => {
                const totalSeconds = milli / 1000;
                const minutes = Math.floor(totalSeconds / 60);
                const seconds = Math.floor(totalSeconds % 60);
                const remaining = Math.floor((totalSeconds - Math.floor(totalSeconds)) * 10);
                let result = '';
                if (minutes > 0) {
                    result += minutes + minute + ' ';
                }
                result += seconds + '.' + remaining;
                if ((0, Global_1.isrtl)()) {
                    result = second + result;
                }
                else {
                    result += second;
                }
                return result;
            };
            const text = new pixi_js_1.Text(timestr(cur), style);
            text.anchor.set(1.0, 0);
            container.addChild(text);
            const countup = () => {
                const diff = Date.now() - start;
                if (diff) {
                    text.text = timestr(cur + diff);
                }
            };
            this.roundtimer = setInterval(countup, 100);
        }
        else if (((_j = this.round) === null || _j === void 0 ? void 0 : _j.type) === interfaces_1.GameType.Practice
            || ((_k = this.round) === null || _k === void 0 ? void 0 : _k.type) === interfaces_1.GameType.Tutorial) {
            const button = this.makeSkipButton();
            button.position.set(-button.width / 2 - (0, Global_1.px)(10), button.height / 2);
            button.visible = false;
            this.skiptimer = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                const alpha = new pixi_js_1.AlphaFilter(0);
                button.filters = [alpha];
                button.visible = true;
                yield this.promisedTimeline(gsap_core_1.gsap.timeline().to(alpha, {
                    pixi: {
                        alpha: 1.0
                    },
                    duration: 1.2,
                    ease: 'power2'
                }));
                button.filters = [];
                alpha.destroy();
            }), config_json_1.default.roundmaxtime);
            container.addChild(button);
        }
        else if (((_l = this.round) === null || _l === void 0 ? void 0 : _l.type) === interfaces_1.GameType.Levels) {
            const button = container.addChild(this.makeSkipButton());
            button.position.set(-button.width / 2 - (0, Global_1.px)(10), button.height / 2);
        }
        return this.addChild(container);
    }
    makeSkipButton() {
        const skipsprite = pixi_js_1.Sprite.from(next_svg_1.default);
        skipsprite.scale.set((0, Global_1.px)(this.overSmallSize ? 0.14 : 0.11));
        const skiptext = new IconButton_1.IconButton(this.app, new pixi_js_1.Text(i18next_1.default.t('skip'), blocks_1.Block.textStyle(this.overSmallSize ? 28 : 22)), skipsprite);
        const button = skiptext.asButton(0.01);
        this.sigs.push(button.onPress.connect(() => {
            var _a;
            if (((_a = this.round) === null || _a === void 0 ? void 0 : _a.type) === interfaces_1.GameType.Levels && !this.gameManager.playerManager.didDraw(this.round.label)) {
                // Make popup
                AssetManager_1.default.untickSound();
                const pop = this.makeSkipPopup();
                pop.didskip = () => {
                    pop.close();
                    this.gameManager.notifySkip();
                };
            }
            else {
                this.gameManager.notifySkip();
            }
        }));
        return button;
    }
    makeSkipPopup() {
        const skipPopup = new SkipPopup_1.SkipPopup(this.app, this.gameManager);
        this.addChild(skipPopup);
        this.layout();
        return skipPopup;
    }
    get canvasoptions() {
        const p = this.isLandscapeBox ? 120 : 10;
        const w = this.app.screen.width / (0, Global_1.px)() - p;
        return {
            strokeWidth: this.overSmallSize ? 8 : 6,
            width: Math.min(w, settings_1.Settings.maxWidth - p),
            height: this.app.screen.height / (0, Global_1.px)() - (this.isLandscapeBox ? 60 : 160)
        };
    }
    enterScene(from, target) {
        const _super = Object.create(null, {
            enterScene: { get: () => super.enterScene }
        });
        return __awaiter(this, void 0, void 0, function* () {
            return _super.enterScene.call(this, from, target);
        });
    }
    exitScene(to, target) {
        const _super = Object.create(null, {
            exitScene: { get: () => super.exitScene }
        });
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            // Find our button
            this.zIndex = SceneManager_1.Layer.Top;
            const mybutton = to.getChildByName('game' + ((_a = this.round) === null || _a === void 0 ? void 0 : _a.type), true);
            if (!this.crash && mybutton instanceof ui_1.ButtonContainer) {
                yield this.animateMask(mybutton, true);
            }
            else if (this.crash || ((to === null || to === void 0 ? void 0 : to.name) === SceneManager_1.Scenes.Home && ((_b = this.round) === null || _b === void 0 ? void 0 : _b.type) === interfaces_1.GameType.Tutorial) || ((to === null || to === void 0 ? void 0 : to.name) === SceneManager_1.Scenes.Levels)) {
                yield this.animateFall();
            }
            else {
                const othersbutton = to.getChildByName('others', true);
                if (othersbutton) {
                    yield this.animateMask(othersbutton, true);
                }
            }
            return _super.exitScene.call(this, to);
        });
    }
    backTo() {
        return (0, Global_1.defaultBackToForType)(this.round.type);
    }
    get cuttime() {
        return (new Date(Math.floor(this.round.time / 1000) + this.round.start + config_json_1.default.roundmaxtime)).getTime();
    }
    get isValid() {
        return this.round && !!this.room;
    }
    get room() {
        return this.gameManager.room$.value;
    }
    layout() {
        var _a, _b, _c, _d, _e, _f, _g;
        super.layout();
        // Maybe resize our canvas
        const size = this.canvasoptions;
        (_a = this.myCanvas) === null || _a === void 0 ? void 0 : _a.resize(size.width, size.height);
        (_b = this.myCanvas) === null || _b === void 0 ? void 0 : _b.pivot.set(this.center.x, 0);
        (_c = this.myCanvas) === null || _c === void 0 ? void 0 : _c.position.set(this.app.screen.width - this.myCanvas.width / 2, (0, Global_1.px)(this.isLandscapeBox ? 25 : 75));
        (_d = this.leftStatus) === null || _d === void 0 ? void 0 : _d.position.set(this.center.x - this.myCanvas.width / 2 + (0, Global_1.px)(5), this.myCanvas.y + (0, Global_1.px)(9));
        const movedown = (((_e = this.room) === null || _e === void 0 ? void 0 : _e.type) === interfaces_1.GameType.Levels && ((_f = this.rightStatus) === null || _f === void 0 ? void 0 : _f.width) > (0, Global_1.px)(85) ? 21 : 14);
        (_g = this.rightStatus) === null || _g === void 0 ? void 0 : _g.position.set(this.center.x + this.myCanvas.width / 2 - (0, Global_1.px)(19), this.myCanvas.y + (0, Global_1.px)(movedown));
        super.layout();
    }
}
exports.ActionScene = ActionScene;
